import { ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import type { Router } from 'vue-router'

import { launchModal, ModalType, ActionTakenType } from '@/stores/modalControl'
import Login from '@/components/Login.vue'

// keep this synchronized with the backend (Conductor.API/Constants/AuthorizationRoles.cs)
export enum LIURole {
  Administrator,
  Student,
  Staff,
  Default,
  Kiosk,
  CalendarAdmin,
  EventScheduler,
  EventAdmin,
  EventProcessor,
  ContentAdmin,
  RequestsAdmin,
  RequestsProcessor,
  TransactionReader,
  StoreAdmin,
  ClassScheduler
}

function mapStringToRole(role: string): LIURole {
  switch (role) {
    case 'Administrator':
      return LIURole.Administrator
    case 'Student':
      return LIURole.Student
    case 'Staff':
      return LIURole.Staff
    case 'Kiosk':
      return LIURole.Kiosk
    case 'CalendarAdmin':
      return LIURole.CalendarAdmin
    case 'EventScheduler':
      return LIURole.EventScheduler
    case 'EventAdmin':
      return LIURole.EventAdmin
    case 'EventProcessor':
      return LIURole.EventProcessor
    case 'ContentAdmin':
      return LIURole.ContentAdmin
    case 'RequestsAdmin':
      return LIURole.RequestsAdmin
    case 'RequestsProcessor':
      return LIURole.RequestsProcessor
    case 'TransactionReader':
      return LIURole.TransactionReader
    case 'StoreAdmin':
      return LIURole.StoreAdmin
    case 'ClassScheduler':
      return LIURole.ClassScheduler
    default:
      return LIURole.Default
  }
}

export const useLoggedInUserStore = defineStore('loggedInUser', () => {
  const isLoggedIn = ref<boolean>(false)
  const roles = ref<LIURole[]>([])
  const expireTime = ref<Date | null>(null)
  const userName = ref<string>('')
  const unreadMessages = ref<number>(0)
  const kioskSession = ref<boolean>(false)

  async function initialize() {
    await checkForCookie()
  }

  async function loginWithUsernamePassword(
    username: string,
    password: string,
    redirectFrom: string | null = null,
    router: Router
  ) {
    if (isLoggedIn.value) {
      await logout()
    }

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Basic ' + btoa(username + ':' + password)
      },
      credentials: 'include',
      body: JSON.stringify({ username: username, password: password })
    }
    const response = await fetch('/api/Login', requestOptions)
    const data = await response.json()

    if (response.status >= 400) {
      isLoggedIn.value = false
      return
    }

    if (data.wasNotAbleToFullyLoad) {
      launchModal({
        message:
          'There was an issue loading your data. Some features may not work correctly. Please contact the ITLP IT team for assistance.',
        header: 'Account Load Issue',
        confirmButtonText: 'Ok',
        type: ModalType.Information
      })
    }

    // augment the logged in user store
    const userExpireTime = new Date(data.expirationTime)
    setLoggedInUser(data.roles, userExpireTime, data.username, data.isKiosk)
    // set a timeout to expire the login
    expireLogin(() => sessionExpired(userName.value, router))
    // redirect to the page the user was trying to use
    if (redirectFrom) {
      router.push(redirectFrom)
    } else {
      router.push('/')
    }

    checkForMessages()
  }

  async function performKioskLogin(
    endpoint: string,
    body: any,
    router: Router,
    redirectFrom: string | null
  ) {
    if (isLoggedIn.value) {
      await logout()
    }

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(body)
    }
    const response = await fetch(endpoint, requestOptions)
    const data = await response.json()

    if (response.status >= 400) {
      isLoggedIn.value = false

      if (response.status === 401) {
        launchModal({
          message: 'Could not log in with provided credentials. Please try again.',
          header: 'Login Error',
          confirmButtonText: 'Ok',
          type: ModalType.Error
        })
      }

      return
    }

    // augment the logged in user store
    const userExpireTime = new Date(data.expirationTime)
    setLoggedInUser(data.roles, userExpireTime, data.username, data.isKiosk)
    // set a timeout to expire the login
    expireLogin(() => sessionExpired(userName.value, router))
    // redirect to the page the user was trying to use
    if (redirectFrom) {
      router.push(redirectFrom)
    } else {
      router.push('/')
    }

    launchModal({
      message:
        "You have been successfully logged into the Kiosk. Please remember to log out when you're done - otherwise, the session will automatically end after 10 minutes.",
      header: 'Logged In',
      confirmButtonText: 'Ok',
      confirmButtonFunction: (data) => {
        data.actionTaken = ActionTakenType.Cancel
      },
      type: ModalType.Information
    })

    checkForMessages()
  }

  async function loginWithBOC(
    bocCardNum: string,
    router: Router,
    redirectFrom: string | null = null
  ) {
    await performKioskLogin('/api/LoginKioskByBuffCard', bocCardNum, router, redirectFrom)
  }

  async function loginWithIdentikeyAndSID(
    identikey: string,
    sid: string,
    router: Router,
    redirectFrom: string | null = null
  ) {
    await performKioskLogin(
      '/api/LoginKioskByIdentikeyAndSID',
      { identikey: identikey, studentId: parseInt(sid) },
      router,
      redirectFrom
    )
  }

  function expireLogin(
    functionToCall: (username: string, router: Router) => void,
    router?: Router
  ) {
    if (expireTime === null || expireTime.value === null || isNaN(expireTime.value.getTime())) {
      return
    }
    const alertTime = expireTime.value.getTime() - new Date().getTime()
    setTimeout(functionToCall, alertTime, router)
  }

  async function sessionExpired(username: string, router: Router) {
    const component = shallowRef(Login)
    const userStore = useLoggedInUserStore()
    if (userStore.userName !== username) {
      return
    }
    if (!userStore.kioskSession) {
      launchModal({
        message: 'Your session has expired. Please log in again.',
        component: component,
        hideActions: true,
        header: 'Session Expired',
        confirmButtonText: 'Cancel',
        confirmButtonFunction: (data) => {
          data.actionTaken = ActionTakenType.Cancel
        },
        type: ModalType.Information
      })
    } else {
      launchModal({
        message: 'Your session has expired. Please log in again.',
        header: 'Session Expired',
        confirmButtonText: 'Ok',
        confirmButtonFunction: (data) => {
          data.actionTaken = ActionTakenType.Cancel
        },
        type: ModalType.Information,
        autoClose: true
      })
      // set the route to home
      router.push('/')
    }
  }

  async function logout() {
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'include'
    }
    await fetch('/api/Logout', requestOptions)

    const userStore = useLoggedInUserStore()
    userStore.isLoggedIn = false
    userStore.roles = []
    userStore.expireTime = null
    userStore.userName = ''
  }

  async function checkForCookie(): Promise<boolean> {
    const response = await fetch('/api/UserCookieData')

    if (response.status >= 400) {
      return false
    }

    if (response.status === 204) {
      // no content
      isLoggedIn.value = false
      return false
    }

    const data = await response.json()
    setLoggedInUser(data.roles, new Date(data.expirationTime), data.username, data.isKiosk)

    return true
  }

  function setRoles(roleStrings: string[]) {
    roles.value = roleStrings.map((role) => {
      const roleEnum = mapStringToRole(role)
      if (roleEnum === null) {
        throw new Error(`Unknown role: ${role}`)
      }
      return roleEnum
    })
  }

  async function updateNumberUnreadMessages() {
    if (!isLoggedIn.value) {
      return
    }
    const response = await fetch('/api/UserMessage/GetNumberUnreadMessages')
    var count = await response.json()
    unreadMessages.value = count
  }

  function hasOneOf(checkedRoles: LIURole[]) {
    return (
      checkedRoles.some((r) => roles.value.includes(r)) ||
      roles.value.includes(LIURole.Administrator)
    )
  }

  function hasAdmin() {
    return hasOneOf([LIURole.Administrator])
  }

  async function setLoggedInUser(
    roleStrings: string[],
    expireTimeIn: Date,
    userNameIn: string,
    isKiosk = false
  ) {
    isLoggedIn.value = true
    setRoles(roleStrings)
    expireTime.value = expireTimeIn
    userName.value = userNameIn
    kioskSession.value = isKiosk
    unreadMessages.value = 0 // set to 0 to make sure the watch is triggered in App.vue
    checkForMessages()
  }

  function checkForMessages() {
    updateNumberUnreadMessages()
    setInterval(updateNumberUnreadMessages, 30000)
  }

  function expireTimer(router: Router) {
    expireLogin(sessionExpired, router)
  }

  return {
    initialize,
    loginWithUsernamePassword,
    loginWithBOC,
    loginWithIdentikeyAndSID,
    logout,
    isLoggedIn,
    roles,
    kioskSession,
    setRoles,
    expireTime,
    userName,
    checkForCookie,
    hasAdmin,
    hasOneOf,
    setLoggedInUser,
    unreadMessages,
    updateNumberUnreadMessages,
    expireTimer
  }
})
